import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  RocketLaunchIcon,
  CubeTransparentIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  CodeBracketIcon,
  QuestionMarkCircleIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const DocumentationPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const sections = [
    {
      icon: RocketLaunchIcon,
      title: 'Getting Started',
      description: 'Quick start guide for new users',
      color: 'text-green-400',
      bgColor: 'bg-green-500/20',
      links: [
        { title: 'Introduction to TEMPLUM', href: '#intro' },
        { title: 'Creating Your Account', href: '#account' },
        { title: 'Connecting Your Wallet', href: '#wallet' },
        { title: 'Understanding the Dashboard', href: '#dashboard' },
      ]
    },
    {
      icon: CubeTransparentIcon,
      title: 'Auction Types',
      description: 'Learn about different auction mechanisms',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/20',
      links: [
        { title: 'English Auctions', href: '#english' },
        { title: 'Dutch Auctions', href: '#dutch' },
        { title: 'Vickrey Auctions', href: '#vickrey' },
        { title: 'Candle Auctions', href: '#candle' },
      ]
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Bidding & Payments',
      description: 'How to participate in auctions',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/20',
      links: [
        { title: 'Placing Bids', href: '#bidding' },
        { title: 'Supported Currencies', href: '#currencies' },
        { title: 'Fee Structure', href: '#fees' },
        { title: 'Winning & Settlement', href: '#settlement' },
      ]
    },
    {
      icon: ShieldCheckIcon,
      title: 'Staking & Tiers',
      description: 'NDH staking benefits and tiers',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/20',
      links: [
        { title: 'Staking Overview', href: '#staking-overview' },
        { title: 'Tier Benefits', href: '#tiers' },
        { title: 'How to Stake', href: '#how-to-stake' },
        { title: 'Unstaking & Rewards', href: '#unstaking' },
      ]
    },
    {
      icon: UserGroupIcon,
      title: 'Governance',
      description: 'DAO participation and voting',
      color: 'text-pink-400',
      bgColor: 'bg-pink-500/20',
      links: [
        { title: 'DAO Overview', href: '#dao-overview' },
        { title: 'Voting Power', href: '#voting-power' },
        { title: 'Creating Proposals', href: '#proposals' },
        { title: 'Voting Process', href: '#voting' },
      ]
    },
    {
      icon: CodeBracketIcon,
      title: 'Developers',
      description: 'Technical documentation and APIs',
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/20',
      links: [
        { title: 'API Reference', href: '/docs/api' },
        { title: 'Smart Contracts', href: '/docs/contracts' },
        { title: 'Webhooks', href: '#webhooks' },
        { title: 'SDK & Libraries', href: '#sdk' },
      ]
    }
  ];

  const faqs = [
    {
      question: 'What wallet can I use with TEMPLUM?',
      answer: 'TEMPLUM supports all major Ethereum-compatible wallets including MetaMask, WalletConnect, Coinbase Wallet, and hardware wallets like Ledger and Trezor.'
    },
    {
      question: 'What are the platform fees?',
      answer: 'TEMPLUM charges a 2.5% fee on successful auction sales. NDH stakers can receive fee discounts of up to 35% depending on their tier level.'
    },
    {
      question: 'How do I get NDH tokens?',
      answer: 'NDH tokens can be purchased on decentralized exchanges like Uniswap or through our partner centralized exchanges. You can also earn NDH through staking rewards.'
    },
    {
      question: 'What happens if I win an auction?',
      answer: 'When you win an auction, the payment is automatically settled through our smart contract. For digital assets (NFTs), they are transferred to your wallet immediately. For physical items, the seller will initiate shipping.'
    },
    {
      question: 'Can I cancel a bid?',
      answer: 'This depends on the auction type. In English and Candle auctions, bids are binding and cannot be cancelled. In Dutch auctions, there is only one winning bid. Vickrey auctions allow bid updates until the auction ends.'
    },
    {
      question: 'How is the Candle auction end time determined?',
      answer: 'Candle auctions use Chainlink VRF (Verifiable Random Function) to determine the exact end time within a predefined window. This ensures fairness and prevents manipulation.'
    }
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-dark-900 to-dark-950">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <BookOpenIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Documentation
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Everything you need to know about using TEMPLUM
            </p>

            {/* Search */}
            <div className="max-w-xl mx-auto relative">
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-dark-800 border border-dark-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 bg-dark-900/50 border-y border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-4">
            <Link to="/docs/api" className="px-4 py-2 bg-dark-800 hover:bg-dark-700 rounded-lg text-gray-300 text-sm transition-colors">
              API Reference
            </Link>
            <Link to="/docs/contracts" className="px-4 py-2 bg-dark-800 hover:bg-dark-700 rounded-lg text-gray-300 text-sm transition-colors">
              Smart Contracts
            </Link>
            <Link to="/whitepaper" className="px-4 py-2 bg-dark-800 hover:bg-dark-700 rounded-lg text-gray-300 text-sm transition-colors">
              Whitepaper
            </Link>
            <a href="#faq" className="px-4 py-2 bg-dark-800 hover:bg-dark-700 rounded-lg text-gray-300 text-sm transition-colors">
              FAQ
            </a>
          </div>
        </div>
      </section>

      {/* Documentation Sections */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sections.map((section, idx) => {
                const Icon = section.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 hover:border-primary-500/50 transition-colors"
                  >
                    <div className={`w-12 h-12 ${section.bgColor} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon className={`w-6 h-6 ${section.color}`} />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">{section.title}</h3>
                    <p className="text-gray-400 text-sm mb-4">{section.description}</p>
                    <ul className="space-y-2">
                      {section.links.map((link, lidx) => (
                        <li key={lidx}>
                          <Link
                            to={link.href}
                            className="flex items-center justify-between text-gray-300 hover:text-primary-400 text-sm transition-colors py-1"
                          >
                            {link.title}
                            <ChevronRightIcon className="w-4 h-4" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started Guide */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8">Quick Start Guide</h2>

            <div className="space-y-6">
              {/* Step 1 */}
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center text-primary-400 font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Connect Your Wallet</h3>
                    <p className="text-gray-400 mb-4">
                      Click the "Connect Wallet" button in the top navigation. Select your preferred wallet (MetaMask, WalletConnect, etc.) and approve the connection request.
                    </p>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <p className="text-sm text-gray-500">
                        <strong className="text-gray-400">Tip:</strong> Make sure you're connected to the Polygon network (Chain ID: 137)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center text-primary-400 font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Get MATIC for Gas</h3>
                    <p className="text-gray-400 mb-4">
                      All transactions on TEMPLUM require MATIC tokens for gas fees. You can purchase MATIC on exchanges and bridge to Polygon, or use the Polygon bridge directly.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center text-primary-400 font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Browse Auctions</h3>
                    <p className="text-gray-400 mb-4">
                      Navigate to the Auctions page to see all active listings. Use filters to find auctions by type (English, Dutch, Vickrey, Candle), category, or price range.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center text-primary-400 font-bold flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Place Your Bid</h3>
                    <p className="text-gray-400 mb-4">
                      Select an auction, review the item details, and place your bid. For English auctions, enter an amount higher than the current bid. Confirm the transaction in your wallet.
                    </p>
                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-sm text-yellow-200">
                        <strong>Important:</strong> Bids are binding. Ensure you have sufficient funds before bidding.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 font-bold flex-shrink-0">
                    5
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Stake NDH (Optional)</h3>
                    <p className="text-gray-400">
                      To unlock fee discounts and additional benefits, stake NDH tokens in the Staking section. Higher stakes mean better tiers with more perks!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <QuestionMarkCircleIcon className="w-8 h-8 text-primary-400" />
              <h2 className="text-3xl font-bold text-white">Frequently Asked Questions</h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-dark-800/50 rounded-xl border border-dark-700"
                >
                  <details className="group">
                    <summary className="p-6 cursor-pointer list-none flex items-center justify-between">
                      <span className="text-white font-medium">{faq.question}</span>
                      <ChevronRightIcon className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" />
                    </summary>
                    <div className="px-6 pb-6">
                      <p className="text-gray-400">{faq.answer}</p>
                    </div>
                  </details>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Help CTA */}
      <section className="py-16 bg-gradient-to-r from-primary-500/20 to-purple-500/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              Still Have Questions?
            </h2>
            <p className="text-gray-300 mb-6">
              Our support team and community are here to help you.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
              >
                Contact Support
              </Link>
              <a
                href="https://discord.gg/templum"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
              >
                Join Discord
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DocumentationPage;
